import { useEditorStore } from "@/store";
import { StringField } from "@/components/fields/StringField";
import { NumberField } from "@/components/fields/NumberField";
import { SelectField } from "@/components/fields/SelectField";
import { BooleanField } from "@/components/fields/BooleanField";
import { PropertyGroup } from "@/components/fields/PropertyGroup";
import { CoordinatesField } from "@/components/fields/CoordinatesField";

interface SourceEditorProps {
  sourceId: string;
  source: Record<string, unknown>;
}

export function SourceEditor({ sourceId, source }: SourceEditorProps) {
  const updateSource = useEditorStore((s) => s.updateSource);

  const set = (key: string, val: unknown) => {
    updateSource(sourceId, { ...source, [key]: val });
  };

  const v = (key: string) => source[key];
  const type = (source.type as string) ?? "";

  return (
    <div>
      <SelectField
        label="Type"
        value={type}
        onChange={(val) => set("type", val)}
        options={[
          { value: "vector", label: "Vector" },
          { value: "raster", label: "Raster" },
          { value: "raster-dem", label: "Raster DEM" },
          { value: "geojson", label: "GeoJSON" },
          { value: "image", label: "Image" },
          { value: "video", label: "Video" },
        ]}
      />

      {/* Vector */}
      {type === "vector" && (
        <>
          <PropertyGroup title="Endpoint" defaultOpen hasValues={!!(v("url") || v("tiles"))}>
            <StringField label="TileJSON URL" value={v("url") as string} onChange={(val) => set("url", val)} placeholder="https://..." />
            <StringField label="Tiles" value={Array.isArray(v("tiles")) ? (v("tiles") as string[]).join("\n") : ""} onChange={(val) => set("tiles", val.split("\n").filter(Boolean))} placeholder="Tile URL template (one per line)" />
          </PropertyGroup>
          <PropertyGroup title="Zoom Range" hasValues={v("minzoom") !== undefined || v("maxzoom") !== undefined}>
            <NumberField label="Min Zoom" value={v("minzoom") as number} onChange={(val) => set("minzoom", val)} min={0} max={24} step={1} />
            <NumberField label="Max Zoom" value={v("maxzoom") as number} onChange={(val) => set("maxzoom", val)} min={0} max={24} step={1} />
          </PropertyGroup>
          <PropertyGroup title="Options" hasValues={!!(v("attribution") || v("promoteId"))}>
            <StringField label="Attribution" value={v("attribution") as string} onChange={(val) => set("attribution", val)} />
            <StringField label="Promote ID" value={v("promoteId") as string} onChange={(val) => set("promoteId", val || undefined)} placeholder="Feature property as ID" />
            <BooleanField label="Volatile" value={v("volatile") as boolean} onChange={(val) => set("volatile", val)} description="Tiles change over time" />
          </PropertyGroup>
        </>
      )}

      {/* Raster */}
      {type === "raster" && (
        <>
          <PropertyGroup title="Endpoint" defaultOpen hasValues={!!(v("url") || v("tiles"))}>
            <StringField label="TileJSON URL" value={v("url") as string} onChange={(val) => set("url", val)} placeholder="https://..." />
            <StringField label="Tiles" value={Array.isArray(v("tiles")) ? (v("tiles") as string[]).join("\n") : ""} onChange={(val) => set("tiles", val.split("\n").filter(Boolean))} placeholder="Tile URL template" />
            <NumberField label="Tile Size" value={(v("tileSize") as number) ?? 512} onChange={(val) => set("tileSize", val)} min={128} max={1024} step={128} />
          </PropertyGroup>
          <PropertyGroup title="Zoom Range" hasValues={v("minzoom") !== undefined || v("maxzoom") !== undefined}>
            <NumberField label="Min Zoom" value={v("minzoom") as number} onChange={(val) => set("minzoom", val)} min={0} max={24} step={1} />
            <NumberField label="Max Zoom" value={v("maxzoom") as number} onChange={(val) => set("maxzoom", val)} min={0} max={24} step={1} />
          </PropertyGroup>
          <PropertyGroup title="Options" hasValues={!!v("attribution")}>
            <StringField label="Attribution" value={v("attribution") as string} onChange={(val) => set("attribution", val)} />
          </PropertyGroup>
        </>
      )}

      {/* Raster DEM */}
      {type === "raster-dem" && (
        <>
          <PropertyGroup title="Endpoint" defaultOpen hasValues={!!(v("url") || v("tiles"))}>
            <StringField label="TileJSON URL" value={v("url") as string} onChange={(val) => set("url", val)} placeholder="https://..." />
            <NumberField label="Tile Size" value={(v("tileSize") as number) ?? 512} onChange={(val) => set("tileSize", val)} min={128} max={1024} step={128} />
          </PropertyGroup>
          <PropertyGroup title="Encoding" defaultOpen hasValues={!!v("encoding")}>
            <SelectField label="Encoding" value={(v("encoding") as string) ?? "mapbox"} onChange={(val) => set("encoding", val)} options={[
              { value: "mapbox", label: "Mapbox" },
              { value: "terrarium", label: "Terrarium" },
              { value: "custom", label: "Custom" },
            ]} />
          </PropertyGroup>
        </>
      )}

      {/* GeoJSON */}
      {type === "geojson" && (
        <>
          <PropertyGroup title="Data" defaultOpen hasValues={!!v("data")}>
            <StringField label="Data URL" value={typeof v("data") === "string" ? (v("data") as string) : ""} onChange={(val) => set("data", val)} placeholder="https://... or inline GeoJSON" />
          </PropertyGroup>
          <PropertyGroup title="Clustering" hasValues={!!v("cluster")}>
            <BooleanField label="Enable" value={v("cluster") as boolean} onChange={(val) => set("cluster", val)} />
            <NumberField label="Radius" value={(v("clusterRadius") as number) ?? 50} onChange={(val) => set("clusterRadius", val)} min={0} max={500} step={10} />
            <NumberField label="Max Zoom" value={v("clusterMaxZoom") as number} onChange={(val) => set("clusterMaxZoom", val)} min={0} max={24} step={1} />
          </PropertyGroup>
          <PropertyGroup title="Options" hasValues={!!(v("generateId") || v("lineMetrics") || v("tolerance"))}>
            <BooleanField label="Generate IDs" value={v("generateId") as boolean} onChange={(val) => set("generateId", val)} />
            <BooleanField label="Line Metrics" value={v("lineMetrics") as boolean} onChange={(val) => set("lineMetrics", val)} description="Required for line-gradient" />
            <NumberField label="Tolerance" value={(v("tolerance") as number) ?? 0.375} onChange={(val) => set("tolerance", val)} min={0} max={5} step={0.05} />
            <NumberField label="Buffer" value={(v("buffer") as number) ?? 128} onChange={(val) => set("buffer", val)} min={0} max={512} step={16} />
          </PropertyGroup>
        </>
      )}

      {/* Image */}
      {type === "image" && (
        <>
          <PropertyGroup title="Image" defaultOpen hasValues={!!v("url")}>
            <StringField label="URL" value={v("url") as string} onChange={(val) => set("url", val)} placeholder="https://example.com/image.png" />
          </PropertyGroup>
          <PropertyGroup title="Position" defaultOpen hasValues={!!v("coordinates")}>
            <CoordinatesField
              label="Coordinates"
              value={v("coordinates") as number[][] | undefined}
              onChange={(val) => set("coordinates", val)}
              description="Four corner coordinates that position the image on the map"
            />
          </PropertyGroup>
        </>
      )}

      {/* Video */}
      {type === "video" && (
        <>
          <PropertyGroup title="Video" defaultOpen hasValues={!!v("urls")}>
            <VideoUrlsField
              urls={Array.isArray(v("urls")) ? (v("urls") as string[]) : []}
              onChange={(val) => set("urls", val)}
            />
          </PropertyGroup>
          <PropertyGroup title="Position" defaultOpen hasValues={!!v("coordinates")}>
            <CoordinatesField
              label="Coordinates"
              value={v("coordinates") as number[][] | undefined}
              onChange={(val) => set("coordinates", val)}
              description="Four corner coordinates that position the video on the map"
            />
          </PropertyGroup>
        </>
      )}
    </div>
  );
}

function VideoUrlsField({ urls, onChange }: { urls: string[]; onChange: (urls: string[]) => void }) {
  const update = (idx: number, val: string) => {
    const next = [...urls];
    next[idx] = val;
    onChange(next);
  };

  const add = () => onChange([...urls, ""]);
  const remove = (idx: number) => onChange(urls.filter((_, i) => i !== idx));

  return (
    <div className="rounded-radius-sm px-1.5 py-1">
      <div className="mb-1 text-[11px] text-text-secondary">Video URLs</div>
      <div className="space-y-1">
        {urls.map((url, i) => (
          <div key={i} className="flex items-center gap-1">
            <span className="w-10 text-[8px] text-text-muted">
              {i === 0 ? "MP4" : i === 1 ? "WebM" : `#${i + 1}`}
            </span>
            <input
              type="text"
              value={url}
              onChange={(e) => update(i, e.target.value)}
              placeholder="https://example.com/video.mp4"
              className="h-6 flex-1 rounded-radius-sm border border-border-subtle bg-surface-base px-2 text-[10px] text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
            />
            <button type="button" onClick={() => remove(i)} className="text-[9px] text-text-muted hover:text-error">✕</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-1 text-[9px] text-accent hover:text-accent-hover">
        + Add format
      </button>
      <div className="mt-1 text-[8px] text-text-muted">
        Add multiple formats (MP4, WebM) for cross-browser fallback
      </div>
    </div>
  );
}
